import { Component, inject, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { WorkerAgreementService } from 'src/app/data/worker-agreement.service';
import {
  WorkerAgreementInfoDto,
  WorkerAgreementAnnexDto,
  WorkerAgreementDetailsResponse,
  AnnexListWithDetailsWagesEntry,
} from 'src/app/models/dtos/annexes/annex-list-with-details-dto';
import { AnnexStatusEnum } from 'src/app/models/enums/annex-status-enum';
import { AnnexImportedBadgeComponent } from 'src/app/shared/components/annex-imported-badge/annex-imported-badge.component';

@Component({
  selector: 'app-worker-agreement-details-panel',
  templateUrl: './worker-agreement-details-panel.component.html',
  styleUrls: ['./worker-agreement-details-panel.component.scss'],
  imports: [CommonModule, TranslateModule, MatIconModule, MatTooltipModule, NgxSpinnerModule, AnnexImportedBadgeComponent],
})
export class WorkerAgreementDetailsPanelComponent implements OnInit, OnChanges {
  @Input() workerAgreementId: number;
  @Input() isAdmin: boolean = false;
  @Input() showNavigationSection: boolean = true;

  private workerAgreementService = inject(WorkerAgreementService);
  private router = inject(Router);
  private spinner = inject(NgxSpinnerService);

  readonly spinnerName = 'annex-panel';

  annexes: WorkerAgreementAnnexDto[] = [];
  workerAgreement: WorkerAgreementInfoDto | null = null;
  openItems = new Set<number>();

  ngOnInit(): void {
    this.loadData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['workerAgreementId'] && !changes['workerAgreementId'].firstChange) {
      this.loadData();
    }
  }

  private loadData(): void {
    this.annexes = [];
    this.workerAgreement = null;
    this.openItems.clear();

    const workerAgreementId = this.workerAgreementId;
    this.spinner.show(this.spinnerName);

    this.workerAgreementService.getWorkerAgreementPanelDetails(workerAgreementId, true).subscribe({
      next: (response: WorkerAgreementDetailsResponse) => {
        if (this.workerAgreementId !== workerAgreementId) {
          return;
        }

        this.annexes = response.Annexes ?? [];
        this.workerAgreement = response.WorkerAgreement ?? null;

        if (this.annexes.length === 1) {
          this.openItems.add(this.annexes[0].AnnexId);
        }

        this.spinner.hide(this.spinnerName);
      },
      error: () => {
        if (this.workerAgreementId !== workerAgreementId) {
          return;
        }

        this.spinner.hide(this.spinnerName);
      },
    });
  }

  get workerFullName(): string {
    return `${this.workerAgreement?.WorkerLastName ?? ''} ${this.workerAgreement?.WorkerFirstName ?? ''}`.trim();
  }

  toggleItem(id: number): void {
    if (this.openItems.has(id)) {
      this.openItems.delete(id);
    } else {
      this.openItems.add(id);
    }
  }

  isOpen(id: number): boolean {
    return this.openItems.has(id);
  }

  getChangedAreas(annex: WorkerAgreementAnnexDto): string[] {
    const areas: string[] = [];
    if (annex.ChangesWage) { areas.push('AnnexPanel.ChangeWage'); }
    if (annex.ChangesJobTitle) { areas.push('AnnexPanel.ChangeJobTitle'); }
    if (annex.ChangesTemporaryWorkplace) { areas.push('AnnexPanel.ChangeTemporaryWorkplace'); }
    if (annex.ChangesSupervisor) { areas.push('AnnexPanel.ChangeSupervisor'); }
    return areas;
  }

  getStatusClass(statusId: number): string {
    switch (statusId) {
      case AnnexStatusEnum.Active: return 'annex-status--active';
      case AnnexStatusEnum.Accepted: return 'annex-status--accepted';
      case AnnexStatusEnum.PendingApproval:
      case AnnexStatusEnum.GeneratingSignedAnnex: return 'annex-status--pending';
      case AnnexStatusEnum.Inactive:
      case AnnexStatusEnum.TerminatedAnnex: return 'annex-status--inactive';
      case AnnexStatusEnum.Draft:
      default: return 'annex-status--draft';
    }
  }

  formatWages(wages: AnnexListWithDetailsWagesEntry[]): string {
    if (!wages?.length) { return '—'; }
    return wages.map(w => `${w.Value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${w.CurrencyName} (${w.WageTypeName})`).join(', ');
  }

  navigateToAnnexRegistry(): void {
    this.router.navigate([
      '/workers', this.workerAgreement?.WorkerId,
      'employmentType', this.workerAgreement?.EmploymentTypeId,
      'agreements', this.workerAgreementId,
      'details',
    ]);
  }
}
